import React, { memo, useEffect } from 'react';
import {
  Route,
  Switch,
  RouteComponentProps,
  useLocation,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'emotion-theming';
import MultilingualModal from '@nykaa/nes-intl/components/PartialScreens';
import { CustomProvider, updatePathname } from '@nykaa/nes-intl-shared';
import { State } from 'shared/store/types';
import Header from 'components/Header';
import Footer from 'components/Footer';
// import GDPR from 'components/GDPR';
import UserBucket from 'components/UserBucket';
import statusCodes from 'constants/statusCodes';
import routes from 'routeConfig';
import Boot from 'shared/components/Boot';
import BaseStyles from 'shared/styles/BaseStyles';
import HeaderPost from 'components/HeaderPost';
import VP from 'shared/components/VisitorPrioritization';
import { isEligible } from 'store/app/selector';
import BackToTop from './BackToTop';
import InitializeExperiment from 'shared/components/Experiment/Initialize';
import InitializeAnalytics from 'shared/components/Analytics/Initialize';
import SignUp from 'components/SignUp';
import CartNudge from './CartNudge';
import InstallNudge from './InstallNudge';
import EmailInfo from 'layouts/Auth/components/Profile/EmailInfo';
import pathsWithoutLangPref from 'constants/pathsWithoutLangPref';
import { setSuperProperty } from 'helpers/utils/user';
import { Notification as OfflineNotification } from 'shared/components/Offline/notification';
import { getAppTheme } from './theme';
import styled from './styled';

interface PageRoute {
  path: string;
  component: any;
  routes?: [object];
  status?: number;
  exact?: boolean;
}

const BodyWrapper = styled.div`
  -webkit-font-smoothing: antialiased;
  @media (min-width: 640px) {
    max-width: 640px;
    margin: 0 auto;
  }
`;
const RoutesWithSubRoutes = (route: PageRoute) => (
  <Route
    path={route.path}
    exact={route.exact}
    render={(props: RouteComponentProps) => {
      if (route.status === statusCodes.NOT_FOUND && props.staticContext) {
        props.staticContext.statusCode = route.status;
      }
      return (
        <route.component
          routes={route.routes} // needed for nested routes
          {...props}
        />
      );
    }}
    fallback={() => <div>Loading... main</div>}
  />
);

const theme = getAppTheme();

const App = () => {
  const location = useLocation();
  const { hash } = location;
  const langConfig = useSelector((state: State) => state.langConfig);
  const { lang, isExperimentEnabled, strings } = langConfig || {};
  const abModalSwitch = useSelector(
    (state: State) => state.remoteConfig?.configs?.AB?.vernacular
  );
  const showMultiLanguageModal = useSelector(
    (state: State) =>
      state.remoteConfig?.switches?.showMultiLanguageModal || false
  );
  const appReducer = useSelector((state: State) => state.appReducer);

  const autoPopUpSwitch =
    showMultiLanguageModal || isEligible(abModalSwitch, appReducer.run);
  if (strings && isExperimentEnabled) {
    location.pathname = updatePathname(location.pathname, Object.keys(strings));
  }
  const { pathname } = useSelector((state: State) => state.appReducer);
  // TODO: need to optimize this code.
  // For now 'lp' is coming in req.url, need to get a proper solution for this.
  if (pathname && pathname.indexOf('/lp') > 0) {
    location.pathname = pathname;
    if (!__SERVER__) {
      const updatedPath = pathname + location?.search + hash;
      window.history.replaceState({}, '', updatedPath);
    }
  }

  const config = {
    lang: lang || 'en',
    isExperimentEnabled: isExperimentEnabled || false,
    strings: strings || {},
    modal: MultilingualModal,
    autoPopUpSwitch,
    pathsToHideModals: pathsWithoutLangPref,
  };

  useEffect(() => {
    setSuperProperty();
  }, []);

  return (
    <>
      <ThemeProvider theme={theme}>
        <CustomProvider config={config}>
          <BaseStyles />
          <Boot />
          <VP />
          <Header />
          <HeaderPost />
          <SignUp />
          <EmailInfo />
          <BodyWrapper>
            <Switch>
              {routes?.map((route, index: number) => (
                <RoutesWithSubRoutes
                  key={index}
                  {...route}
                />
              ))}
            </Switch>
          </BodyWrapper>
          <Footer />
          <BackToTop />
          <CartNudge />
          <InstallNudge />
          {/* <GDPR /> */}
          <UserBucket />
          <InitializeExperiment />
          <InitializeAnalytics />
          <OfflineNotification />
        </CustomProvider>
      </ThemeProvider>
    </>
  );
};

export default memo(App);
